/**
 * Keyword Parser & Competitors — TanStack hooks over
 * /api/keyword-parser/* and /api/competitors/*. Response shapes mirror the
 * backend service dictionaries one-to-one; the page components render them
 * without re-deriving business values.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { SavedListKind, SavedListRow } from "@/lib/saved-keyword-lists";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RunMode = "competitors_seeds" | "seeds_only" | "new_only" | "apple_only";
export type RunKind = "full" | "apple_only";
export type CoreSource = "own" | "borrowed" | "none";
export type KeywordLevel = "high" | "medium" | "low" | "filtered_out";
export type KeywordSource = "parser" | "apple" | "both";
export type FilterReason =
  | "not_confirmed" | "weak_demand" | "thin_results" | "low_confidence" | "edge_fragment"
  /** An Apple suggestion the parser dropped: no core app in its top 10. */
  | "no_core_in_top10";

export type Candidate = {
  adam_id: number;
  track_name: string | null;
  seller_name: string | null;
  primary_genre: string | null;
  icon_url: string | null;
  matched_queries: number;
  best_rank: number | null;
};

export type CoreCompetitor = {
  adam_id: number;
  source: "auto" | "search";
  track_name: string | null;
  seller_name: string | null;
  icon_url: string | null;
  added_at?: string;
  dropped?: boolean;
};

export type NichePosition = { query: string; position: number; adam_id: number };

export type StorefrontStatus = {
  approved: boolean;
  count: number;
  coverage: number | null;
  core_undersized: boolean;
  manual_core: boolean;
  approved_at: string | null;
  new_candidates_count: number;
  dropped_from_serp_count: number;
  metadata_changed: boolean;
  title_snapshot: string | null;
  subtitle_snapshot: string | null;
  drift_serp: number | null;
  drift_core: number | null;
};

export type NotAppliedRow = {
  storefront: string;
  country_name: string;
  tier: number;
  collected_at: string | null;
  state: "ready" | "queued" | "running" | "pending";
  candidates_count: number | null;
};

export type AppliedRow = {
  storefront: string;
  country_name: string;
  tier: number;
  collected_at: string | null;
  competitors_count: number;
  icons: (string | null)[];
  coverage: number | null;
  core_undersized: boolean;
  manual_core: boolean;
  new_candidates_count: number;
  dropped_from_serp_count: number;
  metadata_changed: boolean;
  approved_at: string | null;
};

export type StorefrontsResponse = {
  app_adam_id: number;
  not_applied: NotAppliedRow[];
  applied: AppliedRow[];
  has_active_job: boolean;
};

export type StorefrontDetail = {
  app_adam_id: number;
  storefront: string;
  country_name: string;
  snapshot: { id: number; collected_at: string; title: string | null; subtitle: string | null } | null;
  queue_state: "queued" | "running" | null;
  status: StorefrontStatus | null;
  candidates: Candidate[];
  niche_positions: NichePosition[];
  core: CoreCompetitor[];
  coverage: { covered: number; total: number; ratio: number; need_more: number; state: "ok" | "warn" | "blocked" };
};

export type RecalcCard = {
  storefront: string;
  country_name: string;
  status: StorefrontStatus | null;
  core: CoreCompetitor[];
  new_candidates: Candidate[];
  niche_positions: NichePosition[];
  candidates: Candidate[];
};

export type ParserApp = { id: number; name: string; asa_app_id: number; icon_url: string | null };
export type ParserStorefront = { code: string; name: string; tier: number };

export type SetupResponse = {
  asa_connected: boolean;
  apps: ParserApp[];
  storefronts: ParserStorefront[];
  /** Apple suggestions only: null until an app is picked. */
  apple_only: {
    available: boolean;
    core: Record<string, { core_source: CoreSource; core_storefront: string | null }>;
  } | null;
  active_runs: number;
  run_limit: number;
};

export type PreflightStorefront = {
  storefront: string;
  country_name: string;
  core_approved: boolean;
  core_undersized: boolean;
  existing: { run_id: number; finished_at: string | null; keywords: number; in_progress: boolean } | null;
  in_progress: boolean;
};

export type PreflightResponse = {
  asa_connected: boolean;
  active_runs: number;
  run_limit: number;
  limit_reached: boolean;
  needs_core: boolean;
  storefronts: PreflightStorefront[];
  blocked_by_core: string[];
};

export type ResultRow = {
  run_id: number;
  app_id: number;
  app_name: string;
  app_icon_url: string | null;
  storefront: string;
  country_name: string;
  mode: RunMode;
  keywords: number;
  pool_size: number;
  finished_at: string | null;
  seen: boolean;
  artifact_expires_at: string | null;
  apple_error: boolean;
  core_undersized: boolean;
  recollecting: boolean;
  run_kind: RunKind;
  core_source: CoreSource | null;
  core_storefront: string | null;
  apple_empty: boolean;
};

export type ProgressRow = {
  batch_id: string;
  app_id: number;
  app_name: string;
  storefront: string;
  country_name: string;
  position: number;
  total: number;
  status: "queued" | "submitted" | "running" | "failed";
  mode: RunMode;
  started_at: string;
  mine: boolean;
  storefronts: string[];
  /** Time-weighted by phase (backend §5); null until the first phase is seen. */
  pct: number | null;
  phase: string | null;
  phase_label: string | null;
  error: "apple_unavailable" | "failed" | null;
  run_kind: RunKind | null;
};

export type ResultsResponse = { results: ResultRow[]; progress: ProgressRow[]; unseen: number };

export type PoolKeyword = {
  keyword: string;
  popularity: number | null;
  relevance_top10: number | null;
  /** null only in Apple-suggestions-only runs: no relevance (no approved
   * competitors on any market) or the demand check got no answer. */
  level: KeywordLevel | null;
  filter_reason: FilterReason | null;
  hint_count: number | null;
  hint_pos_best: number | null;
  apple_confirmed: boolean;
  origin: string;
  src: KeywordSource;
  brand: boolean;
  demand_checked?: boolean | null;
  /** Apple's own popularity from its keyword recommendations. In an
   * Apple-suggestions-only run it is also `popularity`. */
  apple_popularity?: number | null;
  /** The parser's own score, kept in Apple-suggestions-only runs. */
  parser_popularity?: number | null;
};

export type RunState = {
  selected: string[];
  negative: string[];
  source_filter: "apple" | "extended";
  relevance_filter: string[];
  sort_key: "popularity" | "relevance" | "keyword";
  sort_dir: "asc" | "desc";
  search_text: string;
  translation_on: boolean;
  overlap_on: boolean;
};

export type RunResult = {
  run_id: number;
  app_id: number | null;
  app_name: string | null;
  app_icon_url: string | null;
  storefront: string;
  country_name: string;
  mode: RunMode;
  has_relevance: boolean;
  run_kind: RunKind;
  core_source: CoreSource | null;
  core_storefront: string | null;
  core_storefront_name: string | null;
  core_undersized: boolean;
  apple_error: boolean;
  finished_at: string | null;
  artifact_expires_at: string | null;
  stats: Record<string, number>;
  keywords: PoolKeyword[];
  translation_available: boolean;
  recollecting: boolean;
  state: RunState;
};

export type KeywordSetRow = {
  id: number;
  app_id: number;
  app_name: string;
  storefront: string;
  country_name: string;
  name: string;
  kind: "campaign" | "negative";
  keywords_count: number;
  created_at: string;
  updated_at: string;
};

export type KeywordSetItem = { text: string; popularity?: number | null; level?: string | null; manual?: boolean };

export type KeywordSet = {
  id: number;
  storefront: string;
  country_name: string;
  name: string;
  kind: "campaign" | "negative";
  keywords: KeywordSetItem[];
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const K = {
  badge: ["kp-badge"] as const,
  setup: (appId: number | null) => ["kp-setup", appId] as const,
  results: ["kp-results"] as const,
  run: (runId: number) => ["kp-run", runId] as const,
  sets: (appId: number | null) => ["kp-sets", appId] as const,
  storefronts: (appId: number) => ["kp-storefronts", appId] as const,
  storefront: (appId: number, cc: string) => ["kp-storefront", appId, cc] as const,
  recalc: (appId: number, cc: string) => ["kp-recalc", appId, cc] as const,
};

// ---------------------------------------------------------------------------
// Keyword Parser
// ---------------------------------------------------------------------------

/** Sidebar badge — unseen results; `enabled:false` hides the whole section. */
export function useKeywordParserBadge(enabled: boolean) {
  return useQuery({
    queryKey: K.badge,
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ enabled: boolean; count: number }>("/api/keyword-parser/badge");
      return data;
    },
    // Real-time enough for the sidebar: a storefront callback shows within 10 s.
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
}

export function useParserSetup(appId: number | null) {
  return useQuery({
    queryKey: K.setup(appId),
    queryFn: async () => {
      const { data } = await api.get<SetupResponse>("/api/keyword-parser/setup", {
        params: appId != null ? { app_id: appId } : undefined,
      });
      return data;
    },
    staleTime: 60_000,
  });
}

export function usePreflight() {
  return useMutation({
    mutationFn: async (body: { app_id: number; storefronts: string[]; mode: RunMode }) => {
      const { data } = await api.post<PreflightResponse>("/api/keyword-parser/preflight", body);
      return data;
    },
  });
}

export function useStartRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      app_id: number;
      storefronts: string[];
      mode: RunMode;
      seed_phrases: string;
      exclude_spend_days: number | null;
      skip_collected: boolean;
    }) => {
      const { data } = await api.post<{ batch_id: string; run_ids: number[]; storefronts: string[] }>(
        "/api/keyword-parser/runs",
        body,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: K.results });
      void qc.invalidateQueries({ queryKey: ["kp-setup"] });
    },
  });
}

/** Results list + running tickers. Polls while any batch is in flight. */
export function useParserResults(enabled = true) {
  return useQuery({
    queryKey: K.results,
    enabled,
    queryFn: async () => {
      const { data } = await api.get<ResultsResponse>("/api/keyword-parser/results");
      return data;
    },
    // §4: the New Keywords counter and the tickers move without a reload —
    // one poll every 10 s, whether or not a run is in flight.
    staleTime: 5_000,
    refetchInterval: 10_000,
  });
}

export function useRunResult(runId: number | null) {
  return useQuery({
    queryKey: K.run(runId ?? 0),
    enabled: runId != null,
    queryFn: async () => {
      const { data } = await api.get<RunResult>(`/api/keyword-parser/runs/${runId}`);
      return data;
    },
    staleTime: 60_000,
  });
}

export function useSaveRunState(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (state: RunState) => {
      await api.put(`/api/keyword-parser/runs/${runId}/state`, state);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: K.badge });
    },
  });
}

export function useResetRunState(runId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/api/keyword-parser/runs/${runId}/state`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: K.run(runId) });
    },
  });
}

/** Pairs the Exact Terms Cleaner would treat as one query — computed once
 * per pool on the server and cached there; fetched only when the Overlap
 * cleaner is switched on. */
export function useRunOverlap(runId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: [...K.run(runId ?? 0), "overlap"] as const,
    enabled: runId != null && enabled,
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await api.get<{ pairs: [string, string][] }>(`/api/keyword-parser/runs/${runId}/overlap`);
      return data.pairs;
    },
  });
}

export function useTranslateRun() {
  return useMutation({
    mutationFn: async (runId: number) => {
      const { data } = await api.post<{ translations: Record<string, string> }>(
        `/api/keyword-parser/runs/${runId}/translate`,
      );
      return data.translations;
    },
  });
}

// ---------------------------------------------------------------------------
// Saved keyword sets (App Settings → Keywords + Save as in results)
// ---------------------------------------------------------------------------

export function useKeywordSets(appId: number | null, enabled = true) {
  return useQuery({
    queryKey: K.sets(appId),
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ sets: KeywordSetRow[] }>("/api/keyword-parser/sets", {
        params: appId != null ? { app_id: appId } : undefined,
      });
      return data.sets;
    },
    staleTime: 30_000,
  });
}

// Campaign Studio panels next to the keyword fields: lists of one or several
// apps without keywords; keywords are fetched only on Preview / Add / Add
// selected (one request for all ids). Refetched on app change (new key) and
// on return to the browser tab. Key under "kp-sets" so Save as / edit /
// delete in the Keyword Parser invalidate it too.
export function useSavedListsBrief(appIds: number[], kind: SavedListKind) {
  const ids = [...new Set(appIds)].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["kp-sets", "brief", kind, ids] as const,
    enabled: ids.length > 0,
    refetchOnWindowFocus: "always",
    staleTime: 30_000,
    queryFn: async () => {
      const qs = [...ids.map((id) => `app_ids=${id}`), `kind=${kind}`].join("&");
      const { data } = await api.get<{ sets: SavedListRow[] }>(`/api/keyword-parser/sets/brief?${qs}`);
      return data.sets;
    },
  });
}

/** Keywords of several lists in one request; ids gone (deleted elsewhere,
 *  or no longer visible) come back in `missing`. */
export async function fetchSavedListKeywords(
  ids: number[],
): Promise<{ sets: { id: number; keywords: string[] }[]; missing: number[] }> {
  const qs = [...new Set(ids)].map((id) => `ids=${id}`).join("&");
  const { data } = await api.get<{ sets: { id: number; keywords: string[] }[]; missing: number[] }>(
    `/api/keyword-parser/sets/keywords?${qs}`,
  );
  return data;
}

export function useKeywordSet(setId: number | null) {
  return useQuery({
    queryKey: ["kp-set", setId],
    enabled: setId != null,
    queryFn: async () => {
      const { data } = await api.get<KeywordSet>(`/api/keyword-parser/sets/${setId}`);
      return data;
    },
  });
}

export function useCreateKeywordSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      app_id: number;
      storefront: string;
      name: string;
      kind: "campaign" | "negative";
      keywords: KeywordSetItem[];
    }) => {
      const { data } = await api.post<KeywordSet>("/api/keyword-parser/sets", body);
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["kp-sets"] }),
  });
}

export function useUpdateKeywordSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: number; name?: string; keywords?: KeywordSetItem[] }) => {
      const { id, ...body } = args;
      const { data } = await api.put<KeywordSet>(`/api/keyword-parser/sets/${id}`, body);
      return data;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["kp-sets"] });
      void qc.invalidateQueries({ queryKey: ["kp-set", v.id] });
    },
  });
}

export function useDeleteKeywordSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/keyword-parser/sets/${id}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["kp-sets"] }),
  });
}

// ---------------------------------------------------------------------------
// Competitors (App Settings → Competitors and Keywords)
// ---------------------------------------------------------------------------

export function useCompetitorStorefronts(appId: number | null) {
  return useQuery({
    queryKey: K.storefronts(appId ?? 0),
    enabled: appId != null,
    queryFn: async () => {
      const { data } = await api.get<StorefrontsResponse>("/api/competitors/storefronts", {
        params: { app_id: appId },
      });
      return data;
    },
    staleTime: 15_000,
    // Poll only while the parser is actually working on this app; markets
    // merely waiting their turn ("pending") don't change on their own.
    refetchInterval: (q) => (q.state.data?.has_active_job ? 10_000 : false),
  });
}

export function useStorefrontDetail(appId: number | null, storefront: string | null) {
  return useQuery({
    queryKey: K.storefront(appId ?? 0, storefront ?? ""),
    enabled: appId != null && !!storefront,
    queryFn: async () => {
      const { data } = await api.get<StorefrontDetail>(`/api/competitors/storefronts/${storefront}`, {
        params: { app_id: appId },
      });
      return data;
    },
    staleTime: 15_000,
    // Countdown screen: poll until the snapshot lands.
    refetchInterval: (q) => (q.state.data && !q.state.data.snapshot ? 3_000 : false),
  });
}

export function useCollectStorefrontNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { appId: number; storefront: string }) => {
      const { data } = await api.post<{ job_id: number; queued: boolean }>(
        `/api/competitors/storefronts/${args.storefront}/collect`,
        undefined,
        { params: { app_id: args.appId } },
      );
      return data;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: K.storefronts(v.appId) });
      void qc.invalidateQueries({ queryKey: K.storefront(v.appId, v.storefront) });
    },
  });
}

export type CompetitorIn = {
  adam_id: number;
  track_name?: string | null;
  seller_name?: string | null;
  icon_url?: string | null;
};

function useCoreMutation(path: (cc: string) => string, method: "post" | "put") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { appId: number; storefront: string; competitors: CompetitorIn[] }) => {
      const url = path(args.storefront);
      const { data } =
        method === "post"
          ? await api.post<StorefrontDetail>(url, { competitors: args.competitors }, { params: { app_id: args.appId } })
          : await api.put<StorefrontDetail>(url, { competitors: args.competitors }, { params: { app_id: args.appId } });
      return data;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: K.storefronts(v.appId) });
      void qc.invalidateQueries({ queryKey: K.storefront(v.appId, v.storefront) });
      void qc.invalidateQueries({ queryKey: K.recalc(v.appId, v.storefront) });
    },
  });
}

export function useApproveStorefront() {
  return useCoreMutation((cc) => `/api/competitors/storefronts/${cc}/approve`, "post");
}

export function useUpdateCore() {
  return useCoreMutation((cc) => `/api/competitors/storefronts/${cc}/core`, "put");
}

export function useApplyRecalc() {
  return useCoreMutation((cc) => `/api/competitors/storefronts/${cc}/recalc`, "post");
}

export function useRecalcCard(appId: number | null, storefront: string | null) {
  return useQuery({
    queryKey: K.recalc(appId ?? 0, storefront ?? ""),
    enabled: appId != null && !!storefront,
    queryFn: async () => {
      const { data } = await api.get<RecalcCard>(`/api/competitors/storefronts/${storefront}/recalc`, {
        params: { app_id: appId },
      });
      return data;
    },
  });
}

export function useSearchApps() {
  return useMutation({
    mutationFn: async (args: { appId: number; storefront: string; term: string }) => {
      const { data } = await api.post<{ results: Candidate[] }>(
        `/api/competitors/storefronts/${args.storefront}/search`,
        { term: args.term },
        { params: { app_id: args.appId } },
      );
      return data.results;
    },
  });
}

export function useRebuildCandidates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (appId: number) => {
      const { data } = await api.post<{ job_id: number; storefronts: number }>(
        "/api/competitors/rebuild",
        undefined,
        { params: { app_id: appId } },
      );
      return data;
    },
    onSuccess: (_d, appId) => void qc.invalidateQueries({ queryKey: K.storefronts(appId) }),
  });
}
